import type { JSONContent } from '@tiptap/core';
import { Fragment, type Mark, type Node as PMNode, type NodeType, type Schema } from '@tiptap/pm/model';

export type Repairs = Record<string, number>;

const count = (repairs: Repairs, kind: string) => {
  repairs[kind] = (repairs[kind] ?? 0) + 1;
};

const knownAttrs = (spec: { attrs?: Record<string, unknown> }, attrs: Record<string, unknown> = {}, repairs: Repairs) =>
  Object.fromEntries(
    Object.entries(attrs).filter(([name]) => {
      const known = name in (spec.attrs ?? {});
      if (!known) count(repairs, 'unknown-attribute');
      return known;
    }),
  );

const textOf = (json: JSONContent): string =>
  json.type === 'text'
    ? (json.text ?? '')
    : (json.content ?? []).map(textOf).join(json.type === 'paragraph' ? '' : ' ');

const marksOf = (schema: Schema, json: JSONContent, repairs: Repairs): Mark[] =>
  (json.marks ?? []).flatMap((mark) => {
    const type = schema.marks[mark.type];
    if (!type) {
      count(repairs, 'unknown-mark');
      return [];
    }
    return [type.create(knownAttrs(type.spec, mark.attrs, repairs))];
  });

const paragraphsOfText = (schema: Schema, text: string): PMNode[] =>
  text.trim() ? [schema.nodes.paragraph.create(null, schema.text(text))] : [];

const createChecked = (type: NodeType, attrs: Record<string, unknown>, children: PMNode[]): PMNode | null => {
  try {
    const node = type.create(attrs, Fragment.from(children));
    node.check();
    return node;
  } catch {
    return null;
  }
};

const wrapForParent = (type: NodeType, child: PMNode) => {
  const wrapping = type.contentMatch.findWrapping(child.type);
  return wrapping ? wrapping.reduceRight((inner, wrapper) => wrapper.create(null, inner), child) : child;
};

const downgrade = (schema: Schema, type: NodeType, attrs: Record<string, unknown>, children: PMNode[]) => {
  if (!type.inlineContent) {
    return children.flatMap((child) => (child.isBlock ? [child] : paragraphsOfText(schema, child.textContent)));
  }
  const inline = children.flatMap((child) =>
    child.isInline ? [child] : child.content.content.filter((n) => n.isInline),
  );
  return [createChecked(type, attrs, inline) ?? schema.nodes.paragraph.create(null, inline)];
};

const fitNode = (schema: Schema, json: JSONContent, repairs: Repairs): PMNode[] => {
  if (json.type === 'text') {
    return json.text ? [schema.text(json.text, marksOf(schema, json, repairs))] : [];
  }
  const type = json.type ? schema.nodes[json.type] : undefined;
  if (!type) {
    count(repairs, 'unknown-node');
    return paragraphsOfText(schema, textOf(json));
  }
  const children = (json.content ?? []).flatMap((child) => fitNode(schema, child, repairs));
  const attrs = knownAttrs(type.spec, json.attrs, repairs);
  const node = createChecked(type, attrs, children);
  if (node) return [node];

  const rewrapped = createChecked(
    type,
    attrs,
    children.map((child) => wrapForParent(type, child)),
  );
  if (rewrapped) {
    count(repairs, 'wrapped-content');
    return [rewrapped];
  }
  count(repairs, `downgraded-${type.name}`);
  return downgrade(schema, type, attrs, children);
};

export const fitToSchema = (schema: Schema, json: JSONContent): { doc: PMNode; repairs: Repairs } => {
  const repairs: Repairs = {};
  const blocks = (json.content ?? [])
    .flatMap((child) => fitNode(schema, child, repairs))
    .flatMap((block) => {
      if (schema.topNodeType.contentMatch.matchType(block.type)) return [block];
      count(repairs, `lifted-${block.type.name}`);
      return block.isTextblock
        ? [schema.nodes.paragraph.create(null, block.content)]
        : paragraphsOfText(schema, block.textContent);
    });
  const doc = createChecked(schema.topNodeType, {}, blocks);
  if (!blocks.length || !doc) throw new Error('No block of the document fits the clause schema');
  return { doc, repairs };
};
